import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import hubService from '../hub/HubConnectionService';
import { NotificacaoService, type ResumoNotificacoes } from '../services/NotificacaoService';

/**
 * Os números do canto superior da sidebar, e o aviso sonoro de mensagem nova.
 * docs/designsystem/propostas/notificacoes.html §8, fase 4.
 *
 * Antes daqui saía um contador de mensagens mantido EM MEMÓRIA: subia com o evento do
 * SignalR, zerava ao entrar em /chat e se perdia num F5 — ele não sabia do que tinha
 * chegado enquanto a pessoa estava fora. O número certo sempre esteve no banco
 * (`unread_count` da conversa, que o próprio chat zera ao abrir a conversa), e agora é
 * ele que aparece, junto dos outros dois, numa chamada só.
 */

const VAZIO: ResumoNotificacoes = { avisosNaoLidos: 0, tarefasVencidas: 0, mensagensNaoLidas: 0, mensagensNovasTotal: 0 };

interface GlobalNotificationContextData {
  isHubConnected: boolean;
  /** Avisos não lidos, tarefas vencidas e mensagens não lidas. */
  resumo: ResumoNotificacoes;
  /** Pede os três números de novo — depois de marcar um aviso, abrir uma conversa, resolver tarefa. */
  recarregarResumo: () => void;
  initializeConnection: () => Promise<void>;
}

const GlobalNotificationContext = createContext<GlobalNotificationContextData>({
  isHubConnected: false,
  resumo: VAZIO,
  recarregarResumo: () => { },
  initializeConnection: async () => { },
});

export const useGlobalNotification = () => useContext(GlobalNotificationContext);

export const GlobalNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isHubConnected, setIsHubConnected] = useState(false);
  const [resumo, setResumo] = useState<ResumoNotificacoes>(VAZIO);
  /* Quantas mensagens havia na última medida — é a comparação que diz se CHEGOU
     alguma, que é o que merece som. Compara a SOMA de mensagens, não o número do badge: o badge conta
     conversas, e a 2ª mensagem do mesmo cliente não o muda — o aviso emudeceria. */
  const naoLidasAnteriores = useRef(0);
  const primeiraMedida = useRef(true);

  const recarregarResumo = useCallback(async () => {
    try {
      const novo = await NotificacaoService.resumo();
      setResumo(novo);

      const naEssaTela = window.location.pathname.includes('/chat');
      const visivel = document.visibilityState === 'visible';
      const chegouMensagem = novo.mensagensNovasTotal > naoLidasAnteriores.current;

      /* Na primeira medida não toca: o que já estava lá não "acabou de chegar", e
         ninguém quer som ao abrir o sistema com 12 mensagens de ontem. */
      if (chegouMensagem && !primeiraMedida.current && (!naEssaTela || !visivel)) {
        try {
          const audio = new Audio('/notification.mp3');
          audio.play().catch(() => { /* o navegador bloqueia autoplay antes da 1ª interação */ });
        } catch { /* som é acessório: nunca pode derrubar o contador */ }

        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const aviso = new Notification('Novas mensagens', {
              body: `Você tem ${novo.mensagensNaoLidas} ${novo.mensagensNaoLidas === 1 ? 'conversa' : 'conversas'} com mensagem sem ler.`,
            });
            aviso.onclick = () => window.focus();
          } catch { /* idem */ }
        }
      }

      naoLidasAnteriores.current = novo.mensagensNovasTotal;
      primeiraMedida.current = false;
    } catch {
      /* Silencioso de propósito: número ausente é melhor que sidebar quebrada. */
    }
  }, []);

  /* A permissão de notificação NÃO é pedida aqui. Até 16/10/2026 este provider pedia sozinho, a
     cada abertura do app e sem toque da pessoa. No Chrome do Android, pedido fechado ou ignorado
     algumas vezes faz o navegador bloquear o site, e aí o "Ativar avisos" (AvisosNoAparelho)
     recebia "negado" na hora, mesmo com a pessoa tocando em Permitir. Quem pede agora é só o
     botão; a permissão que ele concede vale também para o aviso de mensagem acima. */

  /* Uma medida por minuto. O que dá a sensação de tempo real é o SignalR abaixo,
     que pede a medida no instante em que a mensagem chega. */
  useEffect(() => {
    recarregarResumo();
    const t = window.setInterval(recarregarResumo, 60000);
    return () => clearInterval(t);
  }, [recarregarResumo]);

  useEffect(() => {
    let montado = true;
    hubService.startConnection()
      .then(() => { if (montado) setIsHubConnected(true); })
      .catch(err => console.error('Erro ao conectar SignalR Hub Global:', err));
    return () => { montado = false; };
  }, []);

  /* Mensagem nova do cliente (origem 0): a fonte do número continua sendo o servidor;
     o evento só apressa a medida. Escuta o "ConversaAtualizada", que vem de QUALQUER conversa
     da empresa — o "ReceiveNewMessage" só chega de quem está com aquela conversa aberta. */
  const aoAtualizarConversa = useCallback((evento: { idConversa: number; origemMensagem: number }) => {
    if (evento?.origemMensagem !== 0) return;
    recarregarResumo();
  }, [recarregarResumo]);

  useEffect(() => {
    if (isHubConnected) hubService.on('ConversaAtualizada', aoAtualizarConversa);
    return () => { hubService.off('ConversaAtualizada', aoAtualizarConversa); };
  }, [isHubConnected, aoAtualizarConversa]);

  const initializeConnection = useCallback(async () => {
    await recarregarResumo();
  }, [recarregarResumo]);

  return (
    <GlobalNotificationContext.Provider value={{ isHubConnected, resumo, recarregarResumo, initializeConnection }}>
      {children}
    </GlobalNotificationContext.Provider>
  );
};

export default GlobalNotificationContext;
